import {
  INTRANET_CHANNEL_ICONS,
  intranetKnowledgeCategoryPath,
  type IntranetChannelIcon,
  type IntranetKnowledgeCategory,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ChannelIcon } from '@/products/intranet/components/intranet-channels';
import {
  useCreateIntranetKnowledgeCategory,
  useDeleteIntranetKnowledgeCategoryBanner,
  useIntranetKnowledgeCategories,
  useUpdateIntranetKnowledgeCategory,
  useUploadIntranetKnowledgeCategoryBanner,
} from '@/products/intranet/features/intranet/use-intranet-knowledge';
import { INTRANET_LIBRARY_PATH } from '@/products/intranet/intranet-paths';

export function KnowledgeCategoriesManagePage() {
  usePageTitle('Kategorien');
  const categories = useIntranetKnowledgeCategories();
  const create = useCreateIntranetKnowledgeCategory();
  const update = useUpdateIntranetKnowledgeCategory();
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [iconName, setIconName] = useState<IntranetChannelIcon | ''>('');
  const [parentId, setParentId] = useState('');

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    const order = Number(sortOrder);
    try {
      await create.mutateAsync({
        name,
        sortOrder: Number.isInteger(order) ? order : 0,
        iconName: parentId ? null : iconName || null,
        ...(parentId ? { parentId } : {}),
      });
      setName('');
      setSortOrder('0');
      setIconName('');
      setParentId('');
      toast.success(parentId ? 'Unterkategorie angelegt.' : 'Kategorie angelegt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Anlegen fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <p>
        <Link
          to={INTRANET_LIBRARY_PATH}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Zur Wissensdatenbank
        </Link>
      </p>
      <PlatformPageHeader
        title="Kategorien"
        description="Hauptkategorien der Wissensdatenbank, auch wenn noch keine Seite zugeordnet ist."
      />

      <form
        onSubmit={onCreate}
        className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-4"
      >
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="knowledge-category-name">Name</Label>
          <Input
            id="knowledge-category-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={2}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="knowledge-category-order">Reihenfolge</Label>
          <Input
            id="knowledge-category-order"
            inputMode="numeric"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="knowledge-category-icon">Icon</Label>
          <Select
            value={iconName || 'none'}
            onValueChange={(value) =>
              setIconName(value === 'none' ? '' : (value as IntranetChannelIcon))
            }
          >
            <SelectTrigger id="knowledge-category-icon" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Icon</SelectItem>
              {INTRANET_CHANNEL_ICONS.map((icon) => (
                <SelectItem key={icon} value={icon}>
                  {icon}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="knowledge-category-parent">Unterkategorie von</Label>
          <Select
            value={parentId || 'none'}
            onValueChange={(value) => setParentId(value === 'none' ? '' : value)}
          >
            <SelectTrigger id="knowledge-category-parent" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keine, neue Hauptkategorie</SelectItem>
              {(categories.data?.items ?? [])
                .filter((category) => category.level !== 2)
                .map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-4">
          <Button type="submit" disabled={create.isPending}>
            Kategorie anlegen
          </Button>
        </div>
      </form>

      {categories.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Kategorien werden geladen …
        </p>
      ) : categories.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Die Kategorien konnten nicht geladen werden.
        </p>
      ) : (
        <ul className="space-y-3">
          {(categories.data?.items ?? [])
            .filter((category) => category.level !== 2)
            .map((category) => (
              <li key={category.id} className="space-y-2">
                <CategoryRow
                  category={category}
                  pending={update.isPending}
                  onSave={(body) => update.mutateAsync({ id: category.id, body })}
                />
                <ul className="space-y-2 ps-4">
                  {(categories.data?.items ?? [])
                    .filter((child) => child.parent?.id === category.id)
                    .map((child) => (
                      <li key={child.id}>
                        <CategoryRow
                          category={child}
                          pending={update.isPending}
                          onSave={(body) => update.mutateAsync({ id: child.id, body })}
                        />
                      </li>
                    ))}
                </ul>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

function CategoryRow({
  category,
  pending,
  onSave,
}: {
  category: IntranetKnowledgeCategory;
  pending: boolean;
  onSave: (body: {
    name: string;
    sortOrder: number;
    iconName: IntranetChannelIcon | null;
  }) => Promise<unknown>;
}) {
  const uploadBanner = useUploadIntranetKnowledgeCategoryBanner();
  const removeBanner = useDeleteIntranetKnowledgeCategoryBanner();
  const [name, setName] = useState(category.name);
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [iconName, setIconName] = useState(category.iconName ?? '');

  async function save(event: FormEvent) {
    event.preventDefault();
    const order = Number(sortOrder);
    try {
      await onSave({
        name,
        sortOrder: Number.isInteger(order) ? order : category.sortOrder,
        iconName: iconName === '' ? null : (iconName as IntranetChannelIcon),
      });
      toast.success('Kategorie gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-4">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={`knowledge-name-${category.id}`}>Name</Label>
          <Input
            id={`knowledge-name-${category.id}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={2}
          />
          <p className="text-xs text-muted-foreground">
            Kurzname {category.slug}
            {category.pageCount === 0 ? ' · Keine Seiten' : ` · ${category.pageCount} Seiten`}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`knowledge-order-${category.id}`}>Reihenfolge</Label>
          <Input
            id={`knowledge-order-${category.id}`}
            inputMode="numeric"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`knowledge-icon-${category.id}`}>Icon</Label>
          <Select
            value={iconName || 'none'}
            onValueChange={(value) => setIconName(value === 'none' ? '' : value)}
          >
            <SelectTrigger id={`knowledge-icon-${category.id}`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Icon</SelectItem>
              {INTRANET_CHANNEL_ICONS.map((icon) => (
                <SelectItem key={icon} value={icon}>
                  {icon}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-4">
          <Label htmlFor={`knowledge-banner-${category.id}`}>Banner</Label>
          {category.bannerUrl ? (
            <img
              src={`${API_BASE_URL}${category.bannerUrl}`}
              alt=""
              className="h-16 w-full rounded-lg object-cover"
            />
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={`knowledge-banner-${category.id}`}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="h-8 max-w-xs text-xs"
              disabled={uploadBanner.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) {
                  return;
                }
                void uploadBanner.mutateAsync({ id: category.id, file }).then(
                  () => toast.success('Banner gespeichert.'),
                  (error: unknown) =>
                    toast.error(
                      error instanceof ApiRequestError ? error.message : 'Banner fehlgeschlagen.',
                    ),
                );
              }}
            />
            {category.bannerUrl ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={removeBanner.isPending}
                onClick={() => {
                  void removeBanner.mutateAsync(category.id).then(
                    () => toast.success('Banner entfernt.'),
                    (error: unknown) =>
                      toast.error(
                        error instanceof ApiRequestError
                          ? error.message
                          : 'Banner entfernen fehlgeschlagen.',
                      ),
                  );
                }}
              >
                Banner entfernen
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3 sm:col-span-4">
          <ChannelIcon name={category.iconName} />
          <Button type="submit" variant="secondary" disabled={pending}>
            Speichern
          </Button>
          <Link
            to={intranetKnowledgeCategoryPath(category.slug)}
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            Seite öffnen
          </Link>
        </div>
      </form>
    </div>
  );
}
